/**
 * Turning notifications on, from the browser's side.
 *
 * Three things have to line up before a push can arrive — a service worker, a
 * granted permission, and a subscription the API knows about — and each fails
 * differently. They are reported separately rather than as one boolean,
 * because "your browser cannot do this" and "you said no" need different
 * screens: one is a dead end, the other is a decision the person can revisit
 * in their browser settings.
 *
 * **Permission is only ever requested from a click.** Browsers refuse
 * `Notification.requestPermission()` outside a user gesture, and Chrome
 * permanently blocks a site that asks on load. There is no code path here that
 * can be reached without someone pressing something.
 */
import { api } from '@/lib/api';

export type PushState =
  /** No service worker, or no Push API — an older browser, or Safari on iOS
   *  before 16.4, or a page that is not installed to the home screen. */
  | 'unsupported'
  /** The deployment has no VAPID keys, so nothing can be delivered. */
  | 'unavailable'
  /** Supported and available, nobody has been asked yet. */
  | 'prompt'
  /** The person said no. Only they can undo this, in browser settings. */
  | 'denied'
  /** Subscribed, and the API knows about this device. */
  | 'on';

function supported(): boolean {
  return typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && typeof window !== 'undefined'
    && 'PushManager' in window
    && 'Notification' in window;
}

/** The VAPID public key, or null when this deployment cannot push. */
async function publicKey(): Promise<string | null> {
  try {
    return (await api.get<{ publicKey: string | null }>('/push/key')).publicKey;
  } catch {
    return null;
  }
}

/** Base64url to the Uint8Array `PushManager.subscribe` insists on. */
function toBytes(base64url: string): Uint8Array {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** Where things stand, without asking for anything. */
export async function pushState(): Promise<PushState> {
  if (!supported()) return 'unsupported';
  if (!(await publicKey())) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'prompt';

  const registration = await navigator.serviceWorker.ready;
  return (await registration.pushManager.getSubscription()) ? 'on' : 'prompt';
}

/**
 * Ask, subscribe, and tell the API. **Call this from a click and nowhere else.**
 *
 * The subscription is registered with our API before this resolves, because a
 * browser subscription the server does not know about is a device that will
 * never be pushed to and that nothing will ever report as broken.
 */
export async function enablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';

  const key = await publicKey();
  if (!key) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'prompt';

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  const subscription = existing ?? await registration.pushManager.subscribe({
    // Required, and browsers reject `false`: a push that shows nothing is
    // exactly what the notificationclick handler in sw.ts exists to prevent.
    userVisibleOnly: true,
    applicationServerKey: toBytes(key) as BufferSource,
  });

  const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return 'prompt';

  await api.post('/me/push-subscriptions', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  });
  return 'on';
}

/**
 * Turn it off.
 *
 * The API is told FIRST. If the order were reversed and the page closed in
 * between, the browser would have no subscription while the server still had a
 * row — pushes to an endpoint nobody is listening on, forever, until the push
 * service eventually returns 410.
 */
export async function disablePush(): Promise<PushState> {
  if (!supported()) return 'unsupported';

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return 'prompt';

  await api.del('/me/push-subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined);
  await subscription.unsubscribe().catch(() => undefined);
  return 'prompt';
}

/** How long sign-out waits for the API to forget this device. */
const FORGET_TIMEOUT_MS = 1500;

/**
 * On sign-out: stop this browser receiving the person's pushes.
 *
 * Signing out used to leave the subscription in place, so a phone handed on
 * kept showing the previous person's messages on its lock screen (review A1,
 * 2026-10-07). Two halves, in this order:
 *
 *  1. **The API is told while there is still a session to tell it with** —
 *     `DELETE /me/push-subscriptions` needs the access token, so this runs
 *     BEFORE the sign-out clears it, bounded to 1.5s.
 *  2. **The browser unsubscribes**, which works offline. The endpoint dies,
 *     and the push service answers 410 for it from then on.
 *
 * The server also forgets the session's devices when the revoke lands
 * (`auth/service.ts` `logout`), including one sent later from a queue — so
 * an offline sign-out loses nothing by (1) failing.
 *
 * **Never throws, never hangs.** `getRegistration()`, not `ready`: `ready`
 * waits forever on a page with no service worker.
 */
export async function forgetThisDevice(timeoutMs = FORGET_TIMEOUT_MS): Promise<void> {
  if (!supported()) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const giveUp = new Promise<void>((resolve) => { timer = setTimeout(resolve, timeoutMs); });
  const work = (async () => {
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) return;
    await Promise.race([
      api.del('/me/push-subscriptions', { endpoint: subscription.endpoint }).catch(() => undefined),
      giveUp,
    ]);
    await subscription.unsubscribe().catch(() => undefined);
  })().catch(() => undefined);
  try {
    await Promise.race([work, giveUp]);
  } finally {
    clearTimeout(timer);
  }
}
